/**
 * CloseAmcDialog (Iter 59)
 *
 * Primary-Admin-only commercial closure / reopen of an AMC schedule.
 *
 * Operator-driven discontinuation is the SOLE state that hard-blocks the
 * billing lifecycle. Calendar end-date crossing is advisory only and never
 * auto-closes a schedule. Closed schedules:
 *   - block Generate / Offline-record / Pause / Resume / Edit
 *   - are excluded from the auto-scheduler
 *   - REMAIN visible (lists, reports, historical invoices preserved)
 *
 * Backend endpoints:
 *   POST /api/billing/amc/{id}/close   body: { reason, notes }
 *   POST /api/billing/amc/{id}/reopen
 */
import { useEffect, useState } from "react";
import { Lock, LockOpen, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const CLOSE_REASONS = [
    { value: "Completed",     label: "Completed — engagement fulfilled" },
    { value: "Discontinued",  label: "Discontinued — client offboarded" },
    { value: "Waived",        label: "Waived — commercial write-off" },
    { value: "Migrated",      label: "Migrated — moved to a different contract" },
    { value: "Other",         label: "Other (specify in notes)" },
];

export default function CloseAmcDialog({ open, onOpenChange, sched, onDone }) {
    const isClosed = sched?.status === "Closed";
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            setReason("");
            setNotes("");
            setBusy(false);
        }
    }, [open]);

    if (!sched) return null;

    const submitClose = async () => {
        if (!reason) {
            toast.error("Pick a close reason to continue");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/billing/amc/${sched.id}/close`,
                { reason, notes: notes.trim() });
            toast.success(`AMC commercially closed (${reason})`);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to close schedule"));
        } finally {
            setBusy(false);
        }
    };

    const submitReopen = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/billing/amc/${sched.id}/reopen`);
            toast.success(`Schedule reopened (now ${data.status || "Active"})`);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to reopen schedule"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={!isClosed && (!!reason || !!notes.trim())}
            contentClassName="max-w-xl"
            testId="close-amc-dialog"
            srTitle={isClosed ? "Reopen AMC schedule" : "Close AMC schedule"}
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        {isClosed ? (
                            <>
                                <LockOpen size={18} weight="duotone" className="text-emerald-600" />
                                Reopen AMC schedule
                            </>
                        ) : (
                            <>
                                <Lock size={18} weight="duotone" className="text-rose-600" />
                                Commercially close AMC
                            </>
                        )}
                    </div>
                    <div className="text-xs text-slate-500">
                        {isClosed ? (
                            <>
                                Restores the schedule to its prior status (
                                <b>{sched.pre_close_status || "Active"}</b>). Future invoice
                                generation, pause/resume, and edits become available again.
                                Historical invoices are unchanged.
                            </>
                        ) : (
                            <>
                                Hard-blocks all future invoice generation, pause/resume, and
                                edits on this schedule. Historical invoices, payments, and
                                credit notes are <b>preserved verbatim</b>. Reversible at any
                                time via Reopen.
                            </>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-4 py-2">
                <div className="text-sm">
                    <div className="text-xs text-slate-500">AMC</div>
                    <div className="font-medium text-slate-900">{sched.amc_name || sched.product_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{sched.client?.name}</div>
                </div>

                {isClosed ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-700 space-y-1.5" data-testid="close-amc-current-state">
                        <div className="flex items-center gap-2">
                            <Lock size={14} className="text-slate-500" />
                            <span className="font-semibold">Currently closed</span>
                        </div>
                        <div className="text-xs text-slate-600">
                            <span className="reedos-eyebrow text-[10px]">Reason</span>{" "}
                            <span className="text-slate-800">{sched.close_reason || "—"}</span>
                        </div>
                        {sched.close_notes && (
                            <div className="text-xs text-slate-600">
                                <span className="reedos-eyebrow text-[10px]">Notes</span>{" "}
                                <span className="text-slate-800">{sched.close_notes}</span>
                            </div>
                        )}
                        {sched.closed_at && (
                            <div className="text-[11px] text-slate-500">
                                Closed on {sched.closed_at.slice(0, 10)}
                                {sched.closed_by?.email ? ` · by ${sched.closed_by.email}` : ""}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                            <Warning size={14} weight="fill" className="mt-0.5 text-amber-600 shrink-0" />
                            <span>
                                This is the only state that <b>hard-blocks</b> the billing
                                lifecycle. Calendar end-date is advisory and never closes a
                                schedule automatically.
                            </span>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Close reason *</Label>
                            <select
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                                data-testid="close-amc-reason-select"
                            >
                                <option value="">— Pick a reason —</option>
                                {CLOSE_REASONS.map((r) => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Notes (optional)</Label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Operator context, ticket reference, hand-off owner…"
                                rows={3}
                                maxLength={1000}
                                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                                data-testid="close-amc-notes-input"
                            />
                            <div className="text-[10px] text-slate-400 mt-1">{notes.length} / 1000</div>
                        </div>
                    </>
                )}
            </div>
            <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                <FormDialogCancel disabled={busy} testId="close-amc-cancel-btn" />
                {isClosed ? (
                    <Button
                        onClick={submitReopen}
                        disabled={busy}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
                        data-testid="close-amc-reopen-confirm-btn"
                    >
                        <LockOpen size={14} /> {busy ? "Reopening…" : "Reopen schedule"}
                    </Button>
                ) : (
                    <Button
                        onClick={submitClose}
                        disabled={busy || !reason}
                        className="bg-rose-600 hover:bg-rose-500 text-white gap-1.5"
                        data-testid="close-amc-confirm-btn"
                    >
                        <Lock size={14} /> {busy ? "Closing…" : "Close schedule"}
                    </Button>
                )}
            </DialogFooter>
        </FormDialog>
    );
}
